import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  CreateMarketingEventRequest,
  MarketingEvent,
  MarketingEventContentUpdateRequest,
  MarketingEventListItem,
  MarketingEventWorkloadUpdateRequest,
  UpdateMarketingEventRequest,
} from '@models/index';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { createCustomStateSetOnUrlHandler, createStateResetHandler, findAndRemove, findAndReplace } from '../../helpers';
import { addEntity, deleteEntity, getAllWithParams, getOne, StateDictionary, updateEntity } from '../../helpers/store-base';
import { EventsService } from './events.service';

export const ToggleOptionsEvents = ['Active', 'All'] as const;
export type ToggleOptionsEventsName = (typeof ToggleOptionsEvents)[number];

type EventsState = {
  toggle: ToggleOptionsEventsName;
  events: MarketingEventListItem[];
  isLoading: boolean;
  isEventCreateLoading: boolean;
  selectedEvent: MarketingEvent | null;
  isSelectedEventLoading: boolean;
  isFieldExpertsLoading: boolean;
};

const initialState: EventsState = {
  toggle: 'All',
  events: [],
  isLoading: false,
  isEventCreateLoading: false,
  selectedEvent: null,
  isSelectedEventLoading: false,
  isFieldExpertsLoading: false,
};

const eventsDictionary: StateDictionary<MarketingEventListItem> = {
  entityList: 'events',
  listLoading: 'isLoading',
  selectedEntity: 'selectedEvent',
  selectedEntityLoading: 'isSelectedEventLoading',
  entityId: 'id',
  createLoading: 'isEventCreateLoading',
  sortFn: (a, b) => (a.startDate > b.startDate ? -1 : 1),
};

export const EventsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('events-store'),
  withComputed(store => ({
    selectedEventId: computed(() => store.selectedEvent()?.id || null),
  })),

  withMethods((store, eventsService = inject(EventsService), router = inject(Router)) => {
    createStateResetHandler<EventsState>('/marketing/events')(store, initialState);
    createCustomStateSetOnUrlHandler<EventsState>('/marketing/events', { selectedEvent: null })(store);
    const repositoryGetAll = getAllWithParams<MarketingEventListItem, 'Active' | undefined>({
      store,
      service: eventsService,
      dictionary: eventsDictionary,
    });
    const repositoryMethods = {
      getAll: (toggle: ToggleOptionsEventsName) => {
        patchState(store, { toggle });
        repositoryGetAll(toggle === 'Active' ? 'Active' : undefined);
      },

      getOne: getOne<MarketingEvent>({
        store,
        service: eventsService,
        dictionary: eventsDictionary,
      }),

      addEvent: addEntity<MarketingEvent, CreateMarketingEventRequest>({
        store,
        service: eventsService,
        dictionary: eventsDictionary,
        onSuccessCallback: (event: MarketingEvent) => {
          router.navigate(['marketing', 'events', event.id, 'details']);
        },
      }),

      updateEvent: updateEntity<MarketingEvent, UpdateMarketingEventRequest>({
        store,
        service: eventsService,
        dictionary: eventsDictionary,
        onErrorCallback: () => {
          patchState(store, state => ({
            selectedEvent: { ...state.selectedEvent! },
          }));
        },
      }),

      deleteEvent: deleteEntity<MarketingEvent>({
        store,
        service: eventsService,
        dictionary: eventsDictionary,
        onSuccessCallback: () => {
          router.navigate(['marketing', 'events']);
          repositoryMethods.getAll(store.toggle());
        },
      }),

      setWorkloads: rxMethod<{ id: string; workloads: string[] }>(
        pipe(
          tap(() => patchState(store, { isSelectedEventLoading: true })),
          switchMap(({ id, workloads }) =>
            eventsService
              .setWorkloads(
                id,
                workloads.reduce((prev, curr) => {
                  return [...prev, { workloadId: curr }];
                }, [] as MarketingEventWorkloadUpdateRequest),
              )
              .pipe(
                tap(event => {
                  patchState(store, () => ({
                    isSelectedEventLoading: false,
                    selectedEvent: event,
                  }));
                }),
                catchError(() => {
                  patchState(store, state => ({
                    selectedEvent: { ...state.selectedEvent! },
                    isSelectedEventLoading: false,
                  }));
                  return EMPTY;
                }),
              ),
          ),
        ),
      ),

      activate: rxMethod<{ id: string }>(
        pipe(
          tap(() => patchState(store, { isSelectedEventLoading: true })),
          switchMap(({ id }) =>
            eventsService.activate(id).pipe(
              tap(event => {
                patchState(store, () => ({
                  isSelectedEventLoading: false,
                  selectedEvent: event,
                  events: findAndReplace<MarketingEventListItem>(store.events(), event, (e: MarketingEventListItem) => e.id === event.id),
                }));
              }),
              catchError(() => {
                patchState(store, state => ({
                  selectedEvent: { ...state.selectedEvent! },
                  isSelectedEventLoading: false,
                }));
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      deactivate: rxMethod<{ id: string }>(
        pipe(
          tap(() => patchState(store, { isSelectedEventLoading: true })),
          switchMap(({ id }) =>
            eventsService.deactivate(id).pipe(
              tap(event => {
                patchState(store, () => ({
                  isSelectedEventLoading: false,
                  selectedEvent: event,
                  events: findAndReplace<MarketingEventListItem>(store.events(), event, (e: MarketingEventListItem) => e.id === event.id),
                }));
              }),
              catchError(() => {
                patchState(store, state => ({
                  selectedEvent: { ...state.selectedEvent! },
                  isSelectedEventLoading: false,
                }));
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      setContent: rxMethod<{ id: string; content: MarketingEventContentUpdateRequest }>(
        pipe(
          tap(() => patchState(store, { isSelectedEventLoading: true })),
          switchMap(({ id, content }) =>
            eventsService.setContent(id, content).pipe(
              tap(event => {
                patchState(store, () => ({
                  isSelectedEventLoading: false,
                  selectedEvent: event,
                }));
              }),
              catchError(() => {
                patchState(store, state => ({
                  selectedEvent: { ...state.selectedEvent! },
                  isSelectedEventLoading: false,
                }));
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      addFieldExpert: rxMethod<{ eventId: string; fieldExpertId: string }>(
        pipe(
          tap(() => patchState(store, { isFieldExpertsLoading: true })),
          switchMap(({ eventId, fieldExpertId }) =>
            eventsService.addFieldExpert(eventId, fieldExpertId).pipe(
              tap(event => {
                patchState(store, {
                  selectedEvent: event,
                  isFieldExpertsLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isFieldExpertsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      removeFieldExpert: rxMethod<{ eventId: string; fieldExpertId: string }>(
        pipe(
          tap(() => patchState(store, { isFieldExpertsLoading: true })),
          switchMap(({ eventId, fieldExpertId }) =>
            eventsService.deleteFieldExpert(eventId, fieldExpertId).pipe(
              tap(() => {
                const selectedEvent = store.selectedEvent()!;
                patchState(store, {
                  selectedEvent: {
                    ...selectedEvent,
                    fieldExperts: findAndRemove(selectedEvent.fieldExperts, entity => entity.id === fieldExpertId),
                  },
                  isFieldExpertsLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isFieldExpertsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    return {
      ...repositoryMethods,

      goToEventActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['marketing', 'events', id]);
        if (!result) return false;

        if (!store.isSelectedEventLoading()) repositoryMethods.getOne(id);
        return true;
      },

      resetState: (state: Partial<EventsState>) => patchState(store, state),
    };
  }),
);

export interface EventsStoreType {
  readonly events: Signal<MarketingEventListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly isEventCreateLoading: Signal<boolean>;
  readonly selectedEvent: Signal<MarketingEvent | null>;
  readonly isSelectedEventLoading: Signal<boolean>;
  readonly isFieldExpertsLoading: Signal<boolean>;

  readonly selectedEventId: Signal<string | null>;

  readonly getAll: (query: ToggleOptionsEventsName) => void;
  readonly getOne: (id: string) => void;
  readonly addEvent: (dto: CreateMarketingEventRequest) => void;
  readonly updateEvent: (input: { id: string; dto: UpdateMarketingEventRequest }) => void;

  readonly setWorkloads: (input: { id: string; workloads: string[] }) => void;
  readonly goToEventActions: (id: string) => Promise<boolean>;
  readonly deleteEvent: (id: string) => void;
  readonly resetState: (state: Partial<EventsState>) => void;

  readonly activate: (input: { id: string }) => void;
  readonly deactivate: (input: { id: string }) => void;
  readonly setContent: (input: { id: string; content: MarketingEventContentUpdateRequest }) => void;

  readonly addFieldExpert: (params: { eventId: string; fieldExpertId: string }) => void;
  readonly removeFieldExpert: (params: { eventId: string; fieldExpertId: string }) => void;
}
